// eslint-disable-next-line import/no-named-as-default
import Icon from "@mdi/react";
import { FunctionComponent, SyntheticEvent, useEffect, useRef, useState } from "react";

import { Warning } from "@material-ui/icons";
import ListIcon from "@material-ui/icons/List";
import VisibilityIcon from "@material-ui/icons/Visibility";
import VisibilityOffIcon from "@material-ui/icons/VisibilityOff";
import { mdiFactory, mdiMinusCircleOutline } from "@mdi/js";
import { Checkbox, Input, Popover } from "antd";
import classnames from "classnames";
import styled from "styled-components/macro";
import {
  selectIsAwaitingFilter,
  useFilterLandingBinStore
} from "stores/filterLandingBinStore";

import { EntityKind } from "models/entityKind";

import { ErrorBoundary, LEGEND_USAGE_TYPES, Tooltip } from "components/base";
import FocusTreeMap from "components/groupBy/FocusTreeMap";
import { GroupByAutomationHandler } from "components/groupBy/GroupByAutomationHandler";
import { IconSpinner } from "components/icons";
import ChartTreeIcon from "components/icons/ChartTree";
import PlaceholderBin from "components/icons/PlaceholderBin";
import PlaceholderBinDisabled from "components/icons/PlaceholderBinDisabled";

import ActiveFilter from "./ActiveFilter";
import BinSettings from "./BinSettings";
import "./GroupByFilterActivity.scss";
import { useOthersMembersExpansion } from "./OthersMembersExpansion";
import SaveActiveFilter from "./SaveActiveFilter";
import { SavedFilterAutomation } from "./SavedFilterAutomation";
import SavedFilterSelection from "./SavedFilterSelection";
import ToggleButton from "./ToggleButton";
import type { BatchAction } from "./types";
import UndoFilter from "./UndoFilter";
import { Collapse, Panel } from "./collapse";
import {
  ColorPaletteListToggle,
  PresetDropdown,
  PresetFields,
  SortOptionsToggle
} from "./focus";
import BatchOperations from "./focus/BatchOperations";
import { PresetDropdownContext } from "./focus/PresetDropdown";
import { useGroupByFilterController } from "./hooks";
import { useIsLegendBehindFilter } from "./hooks/useIsLegendBehindFilter";
import { ProductionButtonGroup } from "./production";
import {
  ActiveFilterHeader,
  ClearAllButton,
  GroupByLabel,
  GroupByValue,
  GroupChangeTrigger,
  GroupedLegendHeader,
  GroupedLegendHeaderBottom,
  GroupedLegendHeaderTop,
  LegendContent,
  LegendContentHeader,
  LegendList,
  ProductionHeader,
  StyledLegend,
  StyledText,
  Wrapper
} from "./styled";

// Colocated here (not in ./styled.ts) to avoid an import cycle: ActiveFilter.tsx
// imports from the ./hooks barrel (for usePropertyTitleLookup), and the barrel
// re-exports useGroupByFilterController — so styled.ts wrapping ActiveFilter/
// SavedFilterSelection would create styled.ts -> ActiveFilter.tsx -> hooks
// barrel -> useGroupByFilterController.ts -> styled.ts.
const swallowEvent = (event: SyntheticEvent) => {
  event.preventDefault();
  event.stopPropagation();
};
const ignoreCheck = () => undefined;

const SavedFilterSelectionWrapper = styled(SavedFilterSelection)`
  text-overflow: ellipsis;
  max-width: 100%;
`;

const StyledActiveFilter = styled(ActiveFilter)`
  background: var(--active-filter-section-bg);
  flex-grow: 1;
  padding-left: 15px;
`;

export interface GroupByFilterActivityProps {
  entityKind: EntityKind;
}

// EVA-6412 SR-005 (T042/T043) — pure render. Calls the thin controller and
// renders JSX only: zero orchestration logic, zero entityKind branches (all
// resolved once in useGroupByFilterDependencies via c.deps.isWell/c.isWell).
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
const GroupByFilterActivity: FunctionComponent<GroupByFilterActivityProps> = ({
  entityKind
}) => {
  const c = useGroupByFilterController(entityKind);
  // Batch actions run after an awaited search; they must use the latest handlers, not the
  // ones from the render the click happened in.
  const latestControllerRef = useRef(c);
  latestControllerRef.current = c;
  const mountedRef = useRef(true);
  useEffect(
    () => () => {
      mountedRef.current = false;
    },
    []
  );

  // An action waiting on the server search applies only to the search and focus it was asked
  // for: a cleared search or another focus would widen it to a different legend.
  const runBatchAfterSearch = (action: BatchAction, onFlushing?: () => void) => {
    const flushed = c.flushFilteredItems();
    if (!(flushed instanceof Promise)) {
      c.performBatchOption(action);
      onFlushing?.();
      return;
    }
    onFlushing?.();
    const origin = { search: c.focusSearch, property: c.groupByPropertyName };
    void flushed.then(() => {
      const latest = latestControllerRef.current;
      if (
        mountedRef.current &&
        latest.focusSearch === origin.search &&
        latest.groupByPropertyName === origin.property
      ) {
        latest.performBatchOption(action);
      }
    });
  };

  // A saved filter switching in: the legend's rows and checks still belong to the filter
  // before it, so its actions wait. The switch ends as its filter lands, but the legend
  // keeps its rows until the map groups that filter, so the wait lasts until then.
  const isAwaitingFilter =
    useFilterLandingBinStore(selectIsAwaitingFilter) && entityKind === EntityKind.Well;
  const isLegendBehind = useIsLegendBehindFilter(entityKind, c.deps.filterId.id);
  const [isSwitchSettling, setIsSwitchSettling] = useState(false);
  if (isAwaitingFilter && !isSwitchSettling) setIsSwitchSettling(true);
  if (!isAwaitingFilter && isSwitchSettling && !isLegendBehind)
    setIsSwitchSettling(false);
  const isSwitchingFilter = isAwaitingFilter || isSwitchSettling;

  // The tree map has no Others row to search into.
  const {
    items: legendItems,
    othersExpansion,
    isSearchPending: isOthersSearchPending
  } = useOthersMembersExpansion(
    entityKind,
    c.showTreeMap ? "" : c.focusSearch,
    c.legendItems,
    c.filteredItems,
    isSwitchingFilter ? undefined : c.selectOthersMember
  );
  const isSearchPending = c.isFocusSearchPending || isOthersSearchPending;

  const wrapperClassNames = classnames({
    "legend-on": c.isLegendVisible,
    "legend-off": !c.isLegendVisible,
    "filter-on": c.isActiveFilterVisible,
    "filter-off": !c.isActiveFilterVisible
  });

  if (!c.selectedProjectId) {
    return <div>Loading projects...</div>;
  }

  return (
    <Wrapper className={`${wrapperClassNames} focus-activity ${entityKind}`}>
      <Collapse defaultActive={["focus", "legend", "filter"]} onChange={() => null}>
        <Panel
          name="production"
          extra={
            <ProductionButtonGroup
              entityKind={entityKind}
              groupModePopoverOpen={c.groupModePopoverOpen}
              onGroupModePopoverOpenChange={c.setGroupModePopoverOpen}
            />
          }
          title={
            <ProductionHeader>
              {c.isWell ? (
                <>
                  <span>Upstream</span>
                </>
              ) : (
                <>
                  <span>Midstream</span> <Icon path={mdiFactory} size={1.0} />
                </>
              )}
            </ProductionHeader>
          }
        />

        <Panel
          name="focus"
          data-testid="focus-selector-panel"
          extra={
            <PresetDropdownContext.Provider value={true}>
              <PresetDropdown
                entityKind={entityKind}
                onGroupByChange={c.handleGroupByChange}
                onRecentFocusItemClick={c.handleRecentFocusItemClick}
              />
            </PresetDropdownContext.Provider>
          }
          title="Focus"
          toggleTooltipText="Toggle Focus Favourites">
          <PresetFields onSelect={c.handlePresetSelection} entityKind={entityKind} />
        </Panel>

        <Panel
          name="bin"
          data-testid="bin-settings-panel"
          title="Bin Settings"
          extra={
            c.minCountWarningVisible && (
              <Warning
                style={{
                  fontSize: "18px",
                  color: "#FFA500",
                  position: "absolute",
                  left: "125px"
                }}
              />
            )
          }
          toggleTooltipText="Toggle Bin Settings">
          <BinSettings
            bin={c.bin}
            dataType={c.deps.groupBy.dataType}
            entityKind={entityKind}
            canEditBin={c.canEditBin}
            onBinChange={c.updateBin}
            onBinSettingChange={c.updateGroupByBin}
            onLock={c.toggleLockBins}
            onReset={c.resetGroupByBin}
            isMinCountWarningVisible={c.minCountWarningVisible}
            groupByPropertyName={c.groupByPropertyName}
            isLocked={c.isBinSettingsLocked}
          />
        </Panel>

        <Panel
          name="legend"
          data-testid="focus-filter-panel"
          loading={c.isFiltering}
          title={
            c.deps.groupBy.mode ? (
              <GroupedLegendHeader>
                <GroupedLegendHeaderTop>
                  <Tooltip
                    title={
                      c.legendTitle !== c.legendDisplayTitle ? c.legendTitle : undefined
                    }>
                    <StyledText strong>
                      Focus:&nbsp;<span className="no-case">{c.legendDisplayTitle}</span>
                    </StyledText>
                  </Tooltip>
                </GroupedLegendHeaderTop>
                <GroupedLegendHeaderBottom>
                  <GroupByLabel>grouped by</GroupByLabel>
                  <Tooltip
                    title={c.deps.groupBy.mode.title || c.deps.groupBy.mode.property}>
                    <GroupByValue>
                      {c.deps.groupBy.mode.title || c.deps.groupBy.mode.property}
                    </GroupByValue>
                  </Tooltip>
                  <GroupChangeTrigger
                    aria-label="Change grouping field"
                    onClick={(e) => {
                      e.stopPropagation();
                      c.setGroupModePopoverOpen(true);
                    }}>
                    change ▾
                  </GroupChangeTrigger>
                </GroupedLegendHeaderBottom>
              </GroupedLegendHeader>
            ) : (
              <StyledText strong ellipsis>
                Focus:&nbsp;
                <Tooltip title={c.legendTitle}>
                  <span className="no-case">{c.legendDisplayTitle}</span>
                </Tooltip>
              </StyledText>
            )
          }
          toggleTooltipText="Toggle Focus Bins"
          onToggle={c.handleLegendToggle}>
          <LegendContent
            $isDisabled={isSwitchingFilter}
            aria-disabled={isSwitchingFilter}
            // Pointer events are off already; this also stops a keyboard Space or Enter,
            // which arrives as a click, on a row that is still focusable.
            onClickCapture={isSwitchingFilter ? swallowEvent : undefined}
            onContextMenuCapture={isSwitchingFilter ? swallowEvent : undefined}
            data-testid="focus-legend-content">
            <LegendContentHeader>
              <Popover
                content={
                  <BatchOperations
                    onSelect={(action) =>
                      runBatchAfterSearch(action, () => c.setIsBatchOperationsOpen(false))
                    }
                  />
                }
                overlayClassName="batch-options"
                placement="bottomLeft"
                trigger="contextMenu"
                open={c.isBatchOperationsOpen}
                onOpenChange={(visible) => c.setIsBatchOperationsOpen(visible)}>
                <Checkbox
                  data-testid="focus-check-all-checkbox"
                  disabled={isSwitchingFilter}
                  checked={c.isAllChecked}
                  onChange={() =>
                    // Decided at click time: a row click during the wait must not flip it.
                    runBatchAfterSearch(c.hasCheckedItems ? "uncheckAll" : "checkAll")
                  }
                  indeterminate={c.hasCheckedItems && !c.isAllChecked}
                />
              </Popover>

              <Input
                value={c.focusSearch}
                onChange={(e) => c.setFocusSearch(e.target.value)}
                placeholder="Search"
                disabled={isSwitchingFilter}
                onKeyDown={c.handleFocusSearchKeydown()}
                allowClear
                suffix={isSearchPending && <IconSpinner size={12} padding={0} />}
              />
              {/*showGroupsNotInFilter has the negation since button is at its default state when it is true and thus should be grey*/}
              <div>
                <ToggleButton
                  data-testid="only-view-selected-toggle"
                  tooltipText={`Only view selected bins`}
                  value={!c.deps.showGroupsNotInFilter}
                  onClick={c.deps.dispatchToggleShowGroupsNotInFilter}
                  disabled={!c.hasCheckedItems && !c.hasEmptyBin}>
                  {c.deps.showGroupsNotInFilter ? (
                    <VisibilityIcon fontSize="large" />
                  ) : (
                    <VisibilityOffIcon fontSize="large" />
                  )}
                </ToggleButton>
              </div>

              {c.bin.BinType != "Quantile" && c.togglePlaceholderBinsEnabled && (
                <div>
                  <ToggleButton
                    tooltipText={
                      c.placeholderBinsEnabled
                        ? "Disable placeholder bins"
                        : "Enable placeholder bins"
                    }
                    value={c.placeholderBinsEnabled}
                    onClick={c.togglePlaceholderBinsEnabled}>
                    {c.placeholderBinsEnabled ? (
                      <PlaceholderBin />
                    ) : (
                      <PlaceholderBinDisabled />
                    )}
                  </ToggleButton>
                </div>
              )}
              <ToggleButton
                tooltipText={`Switch to ${c.showTreeMap ? "List" : "tree map"}`}
                value={c.showTreeMap}
                onClick={() => c.setShowTreeMap((prev) => !prev)}>
                {c.showTreeMap && <ListIcon fontSize="large" />}
                {c.showTreeMap || <ChartTreeIcon />}
              </ToggleButton>

              <ColorPaletteListToggle
                activePalette={c.activePalette}
                onPaletteSelected={c.handlePaletteSelection}
              />

              <SortOptionsToggle entityKind={entityKind} />
            </LegendContentHeader>

            <LegendList isPickerOpen={c.groupModePopoverOpen}>
              {!c.showTreeMap && (
                <StyledLegend
                  othersExpansion={othersExpansion}
                  allowChecked
                  title={c.legendTitle}
                  items={legendItems}
                  selectedItems={c.selectedLegendItems}
                  contextMenuContent={c.ContextMenuItemsComponent}
                  onChange={c.updateSelectedLegendItems}
                  onClickLock={c.colorLockItem}
                  onCheckChanged={
                    isSwitchingFilter ? ignoreCheck : c.handleLegendCheckChange()
                  }
                  entityKind={entityKind}
                  legendUsage={LEGEND_USAGE_TYPES.FOCUS_PANEL}
                />
              )}

              {c.showTreeMap && (
                <FocusTreeMap
                  items={c.filteredItems}
                  onChange={(items) => c.addSelectedLegendItemsToFilter(items, true)}
                />
              )}
            </LegendList>
          </LegendContent>
        </Panel>

        <Panel
          name="filter"
          data-testid="active-filter-panel"
          toggleTooltipText="Toggle Active Filter"
          loading={c.isFiltering}
          onToggle={c.handleFilterToggle}
          extra={
            <ActiveFilterHeader>
              <ErrorBoundary>
                <SavedFilterSelectionWrapper
                  data-testid={`saved-filter-selection-${entityKind}`}
                  className={`saved-filter-selection-${entityKind}`}
                  entityKind={entityKind}
                  savedFilters={c.savedFilters}
                  selectedFilter={c.selectedFilter}
                  onSelectedFilterChanged={c.onSelectedFilterChanged}
                  isModified={c.isFilterModified}
                />
                <SavedFilterAutomation
                  entityKind={entityKind}
                  savedFilters={c.savedFilters}
                  selectedFilter={c.selectedFilter}
                  onSelectedFilterChanged={c.onSelectedFilterChanged}
                />
              </ErrorBoundary>

              <UndoFilter entityKind={entityKind} />

              {c.projectPermissions?.canEditFilters && !!c.selectedProject && (
                <SaveActiveFilter
                  entityKind={entityKind}
                  project={c.selectedProject}
                  checkedLegendItems={c.checkedLegendItems}
                  filterName={c.selectedFilter?.name}
                  isFilterModified={c.isFilterModified}
                  isFiltering={c.isFiltering}
                  onSaveFilter={c.onSaveFilter}
                />
              )}

              <ClearAllButton
                onClick={c.clearAllPropertiesFilter}
                data-testid="clear-all-filters-button">
                <Icon path={mdiMinusCircleOutline} size={1} />
                Clear all
              </ClearAllButton>
            </ActiveFilterHeader>
          }>
          <StyledActiveFilter
            entityKind={entityKind}
            onNodeRemoved={c.onActiveFilterNodeRemoved}
            onMouseDoubleClick={c.onActiveFilterDoubleClick}
          />
        </Panel>
      </Collapse>
      <GroupByAutomationHandler />
    </Wrapper>
  );
};

export default GroupByFilterActivity;
